import { useCallback } from "react";

import type { CdropdownMode, CdropdownOption } from "./Cdropdown.types";
import { CDROPDOWN_MODE } from "./constants";

interface UseDropdownInteractionHandlersInput {
  mode: CdropdownMode;
  isOpen: boolean;
  closeOnSelect: boolean;
  triggerDisabled: boolean;
  handleClose: () => void;
  setIsOpen: (isOpen: boolean) => void;
  setHoveredIndex: (index: number | null) => void;
  setActiveIndex: (index: number | null) => void;
}

interface UseDropdownInteractionHandlersResult {
  onOptionClick: (option: CdropdownOption) => void;
  onOptionMouseEnter: (input: { index: number; isDisabled: boolean }) => void;
  onOptionMouseLeave: () => void;
  onTriggerClick: () => void;
}

function useDropdownOptionClick({
  closeOnSelect,
  handleClose,
}: {
  closeOnSelect: boolean;
  handleClose: () => void;
}): (option: CdropdownOption) => void {
  return useCallback(
    (option: CdropdownOption) => {
      if (option.disabled) {
        return;
      }

      option.onClick();
      if (closeOnSelect) {
        handleClose();
      }
    },
    [closeOnSelect, handleClose],
  );
}

function useDropdownTriggerClick({
  mode,
  isOpen,
  triggerDisabled,
  handleClose,
  setIsOpen,
}: {
  mode: CdropdownMode;
  isOpen: boolean;
  triggerDisabled: boolean;
  handleClose: () => void;
  setIsOpen: (isOpen: boolean) => void;
}): () => void {
  return useCallback(() => {
    if (mode !== CDROPDOWN_MODE.Trigger) {
      return;
    }

    if (triggerDisabled) {
      return;
    }

    if (isOpen) {
      handleClose();
      return;
    }

    setIsOpen(true);
  }, [handleClose, isOpen, mode, setIsOpen, triggerDisabled]);
}

function useDropdownOptionHoverHandlers({
  setHoveredIndex,
  setActiveIndex,
}: {
  setHoveredIndex: (index: number | null) => void;
  setActiveIndex: (index: number | null) => void;
}): Pick<
  UseDropdownInteractionHandlersResult,
  "onOptionMouseEnter" | "onOptionMouseLeave"
> {
  const onOptionMouseEnter = useCallback(
    (input: { index: number; isDisabled: boolean }) => {
      if (input.isDisabled) {
        return;
      }

      setHoveredIndex(input.index);
      setActiveIndex(input.index);
    },
    [setActiveIndex, setHoveredIndex],
  );

  const onOptionMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, [setHoveredIndex]);

  return {
    onOptionMouseEnter,
    onOptionMouseLeave,
  };
}

export function useDropdownInteractionHandlers({
  mode,
  isOpen,
  closeOnSelect,
  triggerDisabled,
  handleClose,
  setIsOpen,
  setHoveredIndex,
  setActiveIndex,
}: UseDropdownInteractionHandlersInput): UseDropdownInteractionHandlersResult {
  const onOptionClick = useDropdownOptionClick({ closeOnSelect, handleClose });
  const onTriggerClick = useDropdownTriggerClick({
    mode,
    isOpen,
    triggerDisabled,
    handleClose,
    setIsOpen,
  });
  const { onOptionMouseEnter, onOptionMouseLeave } =
    useDropdownOptionHoverHandlers({ setHoveredIndex, setActiveIndex });

  return {
    onOptionClick,
    onOptionMouseEnter,
    onOptionMouseLeave,
    onTriggerClick,
  };
}
